import React, { useEffect, useRef } from 'react';
import { cn } from './cn';
import ScrollStrip from './ScrollStrip';
import Tooltip from './Tooltip';
import GroupedColumnChart from './GroupedColumnChart';

const referenceLineClassName = 'border-t-[1.5px] border-current text-bun-stone';

/** Signed, selectable columns. Callers own aggregation, periods and formatting. */
export default function ColumnChart(props) {
    return props.series ? <GroupedColumnChart {...props} /> : <ValueColumnChart {...props} />;
}

function ValueColumnChart({
    items,
    selectedId,
    onSelect,
    formatValue = String,
    referenceValue,
    referenceLabel,
    secondaryReferenceValue,
    secondaryReferenceLabel,
    color,
    mutedColor,
    variant = 'default',
    'aria-label': ariaLabel,
}) {
    const compact = variant === 'compact';
    const scrollRef = useRef(null);
    const selectedRef = useRef(null);
    useEffect(() => {
        const strip = scrollRef.current;
        const selected = selectedRef.current;
        if (strip && selected) {
            strip.scrollLeft += selected.getBoundingClientRect().left - strip.getBoundingClientRect().left - strip.clientWidth / 2 + selected.clientWidth / 2;
        }
    }, [selectedId, items.length, compact]);
    const referenceLines = [
        { id: 'reference', value: referenceValue, label: referenceLabel, style: 'border-solid' },
        { id: 'secondary', value: secondaryReferenceValue, label: secondaryReferenceLabel, style: 'border-dashed' },
    ].filter(line => Number.isFinite(line.value));
    const references = referenceLines.map(line => line.value);
    const legendItems = referenceLines.filter(line => line.label);
    const maximum = Math.max(0, ...items.map(item => item.value), ...references);
    const minimum = Math.min(0, ...items.map(item => item.value), ...references);
    const upper = maximum || (minimum < 0 ? 0 : 1);
    const range = upper - minimum;
    const position = value => ((upper - value) / range) * 100;
    const baseline = position(0);
    const ticks = [...new Set([upper, (upper + minimum) / 2, minimum])];
    const Component = onSelect ? 'button' : 'div';

    // Passive time series retain their entire reporting window at every width.
    if (variant === 'sparkline') {
        return <div role="group" aria-label={ariaLabel} className="flex h-6 min-w-0 items-end gap-px font-instrument">
            {items.map(item => {
                const tooltip = `${item.label}: ${formatValue(item.value)}${item.isOverride ? ' (edited)' : ''}`;
                const height = item.value ? Math.max(12, Math.abs(item.value) / range * 100) : 12;
                return <Tooltip key={item.id} label={tooltip} portal anchorClassName="relative h-full min-w-0 flex-1">
                    <span role="img" tabIndex={0} aria-label={tooltip} data-column-value={item.value}
                        className={cn('absolute inset-x-0 bottom-0 rounded-t-bun-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bun-blue', item.value ? 'bg-bun-data-sky' : 'bg-bun-line-soft', item.isOverride && 'ring-1 ring-bun-blue')}
                        style={{ height: `${Math.min(100, height)}%`, ...(color && item.value ? { backgroundColor: color } : {}) }} />
                </Tooltip>;
            })}
        </div>;
    }
    if (variant === 'timeline') {
        return <div role="group" aria-label={ariaLabel} className="min-w-0 font-instrument">
            <div className="flex h-20 items-end gap-0.5 sm:h-[140px] sm:gap-1">
                {items.map(item => <Tooltip key={item.id} label={`${item.label}: ${formatValue(item.value)}`} portal anchorClassName="relative h-full min-w-0 flex-1">
                    <span role="img" tabIndex={0} aria-label={`${item.label}: ${formatValue(item.value)}`} data-column-value={item.value}
                        className="absolute inset-x-0 rounded-t-bun-sm bg-bun-data-sky focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bun-blue"
                        style={{ top: `${item.value < 0 ? baseline : position(item.value)}%`, height: `${Math.abs(item.value) / range * 100}%` }} />
                </Tooltip>)}
            </div>
            <div className="mt-2 flex justify-between gap-3 text-bun-meta text-bun-stone"><span>{items[0]?.axisLabel || items[0]?.label}</span><span>{items[items.length - 1]?.axisLabel || items[items.length - 1]?.label}</span></div>
        </div>;
    }

    return (
        <div role="group" aria-label={ariaLabel} className="min-w-0 font-instrument">
        {legendItems.length > 0 && <ul aria-label="Chart legend" className="mb-2 flex flex-wrap gap-x-4 gap-y-2 text-bun-body text-bun-slate sm:justify-end">
            {legendItems.map(line => <li key={line.id} className="flex items-center gap-2">
                <span aria-hidden="true" className={cn('w-6 shrink-0', referenceLineClassName, line.style)} />
                <span>{line.label}</span>
            </li>)}
        </ul>}
        <ScrollStrip ref={scrollRef} variant={compact ? 'filters' : undefined} className={compact ? 'px-0.5 py-1' : 'p-1'}>
        <div className={cn('grid', !compact && 'grid-cols-[48px_minmax(0,1fr)] gap-2')} style={{ minWidth: compact ? items.length * 48 - 4 : 56 + items.length * 48 }}>
            {!compact && <div className="relative mt-8 h-[150px] text-right text-bun-meta tabular-nums text-bun-stone" aria-hidden="true">
                {ticks.map(value => (
                    <span key={value} className="absolute right-0 -translate-y-1/2" style={{ top: `${position(value)}%` }}>
                        {formatValue(value)}
                    </span>
                ))}
            </div>}
            <div className="relative min-w-0">
                <div className={cn('pointer-events-none absolute inset-x-0', compact ? 'top-6 h-24' : 'top-8 h-[150px]')} aria-hidden="true">
                    {!compact && ticks.map(value => (
                        <span key={value} className="absolute inset-x-0 border-t border-bun-line-soft" style={{ top: `${position(value)}%` }} />
                    ))}
                    <span className="absolute inset-x-0 border-t border-bun-line" style={{ top: `${baseline}%` }} />
                    {referenceLines.map(line => <span key={line.id} title={line.label} className={cn('absolute inset-x-0', referenceLineClassName, line.style)} style={{ top: `${position(line.value)}%` }} />)}
                </div>
                <div className={cn('relative grid gap-1', !compact && 'sm:gap-2')} style={{ gridTemplateColumns: `repeat(${items.length || 1}, minmax(0, 1fr))` }}>
                    {items.map(item => (
                        <Component
                            key={item.id}
                            ref={item.id === selectedId ? selectedRef : undefined}
                            type={onSelect ? 'button' : undefined}
                            onClick={onSelect ? () => onSelect(item) : undefined}
                            aria-label={`${item.label}: ${formatValue(item.value)}`}
                            aria-pressed={onSelect ? item.id === selectedId : undefined}
                            className={cn(
                                'group flex min-w-0 flex-col items-stretch rounded-bun-sm text-center text-bun-meta text-bun-stone',
                                onSelect && 'min-w-11 cursor-pointer hover:bg-bun-mist focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bun-blue',
                                item.id === selectedId && 'text-bun-text font-strong'
                            )}
                        >
                            <span className={compact ? 'h-6' : 'h-8'} />
                            <span className={cn('relative block', compact ? 'h-24' : 'h-[150px]')}>
                                <span className={cn('absolute inset-x-0 -translate-y-full pb-1 text-bun-meta font-strong tabular-nums', item.id === selectedId ? 'text-bun-text' : 'text-bun-stone')} style={{ top: `${item.value < 0 ? baseline : position(item.value)}%` }}>{formatValue(item.value)}</span>
                                <span
                                    data-column-value={item.value}
                                    className={cn(
                                        'absolute left-1/2 w-3/5 -translate-x-1/2 rounded-bun-sm',
                                        compact ? 'max-w-[30px]' : 'max-w-10',
                                        item.value < 0 ? 'bg-[color:var(--bun-category-green)]' : item.id === selectedId ? 'bg-bun-ink' : 'bg-bun-line'
                                    )}
                                    style={{
                                        backgroundColor: color ? (item.id === selectedId ? color : mutedColor || color) : undefined,
                                        top: `${item.value < 0 ? baseline : position(item.value)}%`,
                                        height: `${Math.abs(item.value) / range * 100}%`,
                                    }}
                                >
                                    {color && item.id !== selectedId && <span className="absolute inset-0 rounded-bun-sm" style={{ backgroundColor: color, opacity: 0.55 }} />}
                                </span>
                            </span>
                            <span className={cn('flex items-center justify-center px-1 leading-snug', compact ? 'min-h-8' : 'min-h-11')}>{item.axisLabel || item.label}</span>
                        </Component>
                    ))}
                </div>
            </div>
        </div>
        </ScrollStrip>
        </div>
    );
}
